import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api, ApiError, fmtTime, TOPICS, type Issue, type Published, type SpreadMeta, type Validation, type VersionInfo } from "../api";
import type { Spread } from "../bdl/model";
import { Card } from "../components/Card";
import { Chain } from "../components/Chain";
import { Figure } from "../components/Figure";

/**
 * Review one chain: the first beat as the reader meets it, every beat, every
 * figure, the sources, the JSON, the history. Approve publishes to the CDN
 * within seconds; every save is validated and kept as a version.
 */
export function SpreadDetail() {
  const { id = "" } = useParams();
  const [search] = useSearchParams();
  const isNew = search.get("new") === "1";
  const nav = useNavigate();
  const [spread, setSpread] = useState<Spread>();
  const [meta, setMeta] = useState<SpreadMeta>();
  const [validation, setValidation] = useState<Validation>();
  const [json, setJson] = useState("");
  const [tab, setTab] = useState<"card" | "chain" | "figures" | "meta" | "json" | "history">(isNew ? "json" : "card");
  const [form, setForm] = useState<{ topic: string; tags: string; difficulty: number; kind: string; feed: boolean }>();
  const [msg, setMsg] = useState<{ ok: boolean; text: string; issues?: Issue[] }>();
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<VersionInfo[]>();

  const load = async () => {
    try {
      if (isNew && !meta) {
        const r = await api<{ spread: Spread }>(`/admin/spreads/${id}/blank`);
        setSpread(r.spread); setJson(JSON.stringify(r.spread, null, 2));
      } else {
        const r = await api<{ spread: Spread; meta: SpreadMeta; validation: Validation }>(`/admin/spreads/${id}`);
        setSpread(r.spread); setJson(JSON.stringify(r.spread, null, 2));
        setMeta(r.meta); setValidation(r.validation);
        setForm({ topic: r.spread.topic, tags: (r.spread.tags ?? []).join(", "), difficulty: r.spread.difficulty ?? 1, kind: r.spread.kind, feed: r.spread.feed !== false });
      }
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  useEffect(() => { void load(); }, [id]);
  useEffect(() => { if (tab === "history" && meta) api<{ versions: VersionInfo[] }>(`/admin/spreads/${id}/versions`).then((r) => setVersions(r.versions)); }, [tab, meta?.version]);

  const publishedNote = (p?: Published) => (p ? ` Published ${p.published} to the CDN.` : "");

  const review = async (status: string) => {
    setBusy(true); setMsg(undefined);
    try {
      const r = await api<{ meta: SpreadMeta; validation: Validation; published?: Published }>(`/admin/spreads/${id}/review`, { method: "PUT", body: { status } });
      setMsg({ ok: true, text: `Now ${status}.${publishedNote(r.published)}` });
      await load();
    } catch (e) { const ex = e as ApiError; setMsg({ ok: false, text: ex.message, issues: ex.issues }); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setMsg(undefined);
    try {
      const parsed = JSON.parse(json) as Spread;
      const r = await api<{ meta: SpreadMeta; validation: Validation; published?: Published }>(`/admin/spreads/${id}`, { method: "PUT", body: parsed });
      setMsg({ ok: true, text: `Saved as v${r.meta.version}. ${r.validation.errors.length} errors, ${r.validation.warnings.length} warnings.${publishedNote(r.published)}`, issues: r.validation.issues });
      if (isNew) nav(`/admin/spreads/${id}`, { replace: true });
      await load();
    } catch (e) { const ex = e as ApiError; setMsg({ ok: false, text: ex.message, issues: ex.issues }); }
    finally { setBusy(false); }
  };

  const saveMeta = async () => {
    if (!spread || !form) return;
    setBusy(true); setMsg(undefined);
    try {
      const next = { ...spread, topic: form.topic, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 4), difficulty: form.difficulty, kind: form.kind, feed: form.feed } as Spread;
      const r = await api<{ meta: SpreadMeta; validation: Validation; published?: Published }>(`/admin/spreads/${id}`, { method: "PUT", body: next });
      setMsg({ ok: true, text: `Saved as v${r.meta.version}.${publishedNote(r.published)}`, issues: r.validation.issues });
      await load();
    } catch (e) { const ex = e as ApiError; setMsg({ ok: false, text: ex.message, issues: ex.issues }); }
    finally { setBusy(false); }
  };

  // The line is what the card shows large; it is edited here, per beat, and
  // nowhere else — the store is the source of truth.
  const saveLines = async (beats: Spread["chain"]["beats"]) => {
    if (!spread) return;
    setBusy(true); setMsg(undefined);
    try {
      const next = { ...spread, chain: { ...spread.chain, beats } };
      const r = await api<{ meta: SpreadMeta; validation: Validation; published?: Published }>(`/admin/spreads/${id}`, { method: "PUT", body: next });
      setMsg({ ok: true, text: `Saved as v${r.meta.version}.${publishedNote(r.published)}`, issues: r.validation.issues });
      await load();
    } catch (e) { const ex = e as ApiError; setMsg({ ok: false, text: ex.message, issues: ex.issues }); }
    finally { setBusy(false); }
  };

  const check = async () => {
    setMsg(undefined);
    try {
      const v = await api<Validation>("/admin/validate", { method: "POST", body: JSON.parse(json) });
      setMsg({ ok: v.ok, text: v.ok ? `Valid. ${v.warnings.length} warnings.` : `${v.errors.length} errors, ${v.warnings.length} warnings.`, issues: v.issues });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };

  const restore = async (at: string) => {
    if (!confirm(`Make the version from ${fmtTime(at)} current? It becomes a draft.`)) return;
    setBusy(true);
    try { await api(`/admin/spreads/${id}/restore`, { method: "POST", body: { at } }); setMsg({ ok: true, text: "Restored as a draft." }); await load(); setTab("card"); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Delete "${spread?.card.title || id}"? It leaves the CDN if it was published. History is kept.`)) return;
    await api(`/admin/spreads/${id}`, { method: "DELETE" });
    nav("/admin/spreads");
  };

  if (!spread) return <>{msg && <div className="msg err">{msg.text}</div>}<p className="chrome">Loading {id}…</p></>;
  const status = meta?.status ?? "draft";
  const hasErrors = (validation?.errors.length ?? 0) > 0;

  return (
    <>
      <p className="chrome"><Link to="/admin/spreads">← Spreads</Link></p>
      <h2>{spread.card.title || id} <span className={`pill ${status}`}>{isNew ? "new" : status}</span></h2>
      <p className="sub">
        {spread.id} · {spread.topic} · {spread.kind} · difficulty {spread.difficulty ?? 1}
        {meta && <> · v{meta.version} by {meta.updatedBy} {fmtTime(meta.updatedAt)}</>}
        {meta?.publishedAt && <> · on the CDN since {fmtTime(meta.publishedAt)}</>}
        {meta?.reviewedAt && <> · reviewed {meta.reviewedAt}</>}
      </p>

      {!isNew && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button className="btn primary" disabled={busy || status === "approved" || hasErrors} title={hasErrors ? "Fix the validation errors first" : ""} onClick={() => review("approved")}>Approve &amp; publish</button>
          <button className="btn" disabled={busy || status === "needs-work"} onClick={() => review("needs-work")}>Needs work</button>
          <button className="btn" disabled={busy || status === "draft"} onClick={() => review("draft")}>Back to draft</button>
          <span style={{ flex: 1 }} />
          <button className="btn danger" disabled={busy} onClick={remove}>Delete</button>
        </div>
      )}
      {validation && !validation.ok && tab !== "json" && (
        <div className="msg err">{validation.errors.length} validation errors — open the JSON tab to see and fix them. An approved chain with errors is never published.</div>
      )}
      {msg && <Message m={msg} />}

      <div style={{ display: "flex", gap: 8, margin: "12px 0 20px" }}>
        {(["card", "chain", "figures", "meta", "json", "history"] as const).map((t) => <button key={t} className={`btn${tab === t ? " primary" : ""}`} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === "card" && (
        <div className="split">
          <Card spread={spread} />
          <div>
            <div className="head">Review checklist</div>
            <ol style={{ paddingLeft: 20 }}>
              <li>Beat 1: does the figure show the thing happening, not described?</li>
              <li>Is every beat a forced step, with its <em>because</em> on the page?</li>
              <li>Does the obvious fix fail for a reason the reader can check?</li>
              <li>Is the mechanism true? Simplified is fine; wrong is a stop.</li>
              <li>Does each figure show what its beat says, and loop cleanly?</li>
              <li>Is the name held back until the thing is understood?</li>
              <li>Voice: do sentence lengths vary (content/voice/meter.py)? No hype, no exclamation marks.</li>
              <li>Is every dated or attributed claim sourced to a page someone opened?</li>
            </ol>
            <div className="head" style={{ marginTop: 24 }}>Sources</div>
            <ul style={{ paddingLeft: 20 }}>
              {(spread.sources ?? []).length === 0 && <li className="chrome">None yet.</li>}
              {(spread.sources ?? []).map((s, i) => <li key={i}>{s.claim}<br /><a className="chrome" href={s.url} target="_blank" rel="noreferrer">{s.url}</a></li>)}
            </ul>
          </div>
        </div>
      )}
      {tab === "chain" && (
        <>
          <ChainLines spread={spread} busy={busy} onSave={saveLines} />
          <Chain spread={spread} />
        </>
      )}
      {tab === "figures" && (
        <div className="figgrid">
          {Object.entries(spread.figures).map(([fid, f]) => (
            <div className="cell" key={fid}>
              <Figure figure={f} size={240} />
              <div className="chrome" style={{ marginTop: 8 }}>{fid}{fid === spread.card.figureId ? " · card" : ""}{f.kind === "scene" ? " · scene" : ""} · {f.parts.length} parts{f.timeline ? ` · ${f.timeline.duration} ms` : " · static"}</div>
            </div>
          ))}
        </div>
      )}
      {tab === "meta" && form && (
        <div style={{ maxWidth: 480 }}>
          <label className="lbl">Category (topic)</label>
          <select className="field" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}>
            {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="lbl">Tags, comma-separated, up to four</label>
          <input className="field" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="locks, security" />
          <label className="lbl">Difficulty</label>
          <select className="field" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: Number(e.target.value) })}>
            <option value={1}>I — the basics</option><option value={2}>II</option><option value={3}>III — advanced</option>
          </select>
          <label className="lbl">Kind</label>
          <select className="field" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="invented">invented — the problem is a human need</option>
            <option value="natural">natural — the problem is a puzzle</option>
          </select>
          <label className="lbl">Where it appears</label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontFamily: "var(--sans)", fontSize: 14 }}>
            <input type="checkbox" checked={form.feed} onChange={(e) => setForm({ ...form, feed: e.target.checked })} /> In the feed (unticked: held back, not published)
          </label>
          <button className="btn primary" style={{ marginTop: 16 }} disabled={busy} onClick={saveMeta}>Save</button>
        </div>
      )}
      {tab === "json" && (
        <>
          <textarea className="json" value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="btn primary" disabled={busy} onClick={save}>{isNew ? "Create" : "Save"}</button>
            <button className="btn" disabled={busy} onClick={check}>Check</button>
            <button className="btn" disabled={busy} onClick={() => setJson(JSON.stringify(spread, null, 2))}>Reset</button>
          </div>
          <p className="chrome" style={{ marginTop: 8 }}>Every save is validated and kept as a version. Drafts may carry errors while you work; approval can't. Editing an approved chain republishes it.</p>
        </>
      )}
      {tab === "history" && (
        <table className="tbl">
          <thead><tr><th>When</th><th>Status</th><th>By</th><th>Note</th><th>Errors</th><th></th></tr></thead>
          <tbody>
            {!versions && <tr><td colSpan={6} className="chrome">Loading…</td></tr>}
            {versions?.map((v, i) => (
              <tr key={v.at}>
                <td>{fmtTime(v.at)}{i === 0 ? <span className="chrome"> · current</span> : ""}</td>
                <td><span className={`pill ${v.status}`}>{v.status}</span></td>
                <td>{v.by}</td><td className="chrome">{v.note}</td><td className="num">{v.errors}</td>
                <td>{i > 0 && <button className="btn" disabled={busy} onClick={() => restore(v.at)}>Restore</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Message({ m }: { m: { ok: boolean; text: string; issues?: Issue[] } }) {
  return (
    <div className={`msg ${m.ok ? "ok" : "err"}`}>
      {m.text}
      {m.issues && m.issues.length > 0 && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
          {m.issues.map((i, k) => <li key={k}><strong>{i.severity === "error" ? "ERROR" : "warn"}</strong> <code>{i.path}</code> — {i.message}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Every beat's line, editable, with the head beside it for reference. */
function ChainLines({ spread, busy, onSave }: { spread: Spread; busy: boolean; onSave: (beats: Spread["chain"]["beats"]) => void }) {
  const original = spread.chain.beats;
  const [beats, setBeats] = useState(original);
  useEffect(() => setBeats(original), [spread]);
  const dirty = beats.some((b, i) => b.line !== original[i]?.line);
  const words = (t?: string) => (t ?? "").trim().split(/\s+/).filter(Boolean).length;
  return (
    <div style={{ maxWidth: 640, marginBottom: 32 }}>
      <div className="head">Lines</div>
      <div className="chrome" style={{ marginBottom: 12 }}>The one sentence each beat shows large on the card, above its body. At most 20 words. A beat without one shows its head instead, which is a label where a sentence belongs.</div>
      {beats.map((b, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <label className="lbl">{i + 1} · {b.role} · <span className="chrome">{b.head}</span> · {words(b.line)} words</label>
          <input className="field" value={b.line ?? ""} placeholder={b.head}
            onChange={(e) => setBeats(beats.map((x, j) => (j === i ? { ...x, line: e.target.value } : x)))} />
        </div>
      ))}
      <button className="btn primary" disabled={busy || !dirty} onClick={() => onSave(beats.map((b) => ({ ...b, line: b.line?.trim() || undefined })))}>Save lines</button>
    </div>
  );
}
